import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, first, take } from 'rxjs/operators';
import { SessionStorageService } from 'ngx-webstorage';
import { LANGUAGES } from '@supoui/core/language/language.constants';
import { AccountService } from '@supoui/core/auth/account.service';
import { ProfileService } from '../../layouts/profiles/profile.service';
import { FilterRequestService } from '../../board/components/cases/travel/services/filter-request.service';
import { AccountRoleService } from '@supoui/core/auth/account-role.service';
import { UserService } from '@supoui/core/user/user.service';
import { SidenavService } from '@supoui/core/services/sidenav.service';
import { CustomerProfiles, CustomerProfilesAdapter } from '@supoui/shared/model/customerProfiles';
import { SupplierProfileService } from '@supoui/core/services/supplier-profile.service';
import { SupplierProfile } from '@supoui/shared/model/supplier-profile';
import { NGXLogger } from 'ngx-logger';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SupoCaseInvoiceService } from '@supoui/core/services/supo-case-invoice.service';
import { TranslateService } from '@ngx-translate/core';
import { CustomerProfileService } from '@supoui/core/services/customer-profile.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  standalone: false,
})
@UntilDestroy({ arrayName: 'subscriptions' })
export class SidenavComponent implements OnInit {
  static readonly BATCH_BILLING_TYPE_ID: number = 1;

  inProduction?: boolean;
  languages = LANGUAGES;
  swaggerEnabled?: boolean;
  isAdminMenuSelected = false;

  customerProfiles: CustomerProfiles[];
  smsQrDraftCaseCount = 0;
  error: any;

  private supplierProfiles: SupplierProfile[] = [];

  subscriptions: Subscription[] = [];

  constructor(
    private translateService: TranslateService,
    private sessionStorage: SessionStorageService,
    private accountService: AccountService,
    private profileService: ProfileService,
    private supplierProfileService: SupplierProfileService,
    private customerProfileService: CustomerProfileService,
    private filterRequestService: FilterRequestService,
    public accountRoleService: AccountRoleService,
    private userService: UserService,
    private supoCaseInvoiceService: SupoCaseInvoiceService,
    private sidenavService: SidenavService,
    private customerProfilesAdapter: CustomerProfilesAdapter,
    private router: Router,
    private logger: NGXLogger
  ) {}

  ngOnInit(): void {
    this.loadProfiles();
    this.loadSupplierProfiles();
    this.loadSmsQrDraftCaseCount();

    this.subscriptions.push(
      this.profileService.getProfileInfo().subscribe(profileInfo => {
        this.inProduction = profileInfo.inProduction;
        this.swaggerEnabled = profileInfo.swaggerEnabled;
      })
    );

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        first()
      )
      .subscribe(() => this.sidenavService.close());
  }

  isCasesRouteActive(): boolean {
    return this.router.url.includes('/cases');
  }

  isInsightsRouteActive(): boolean {
    return this.router.url.includes('/insights');
  }

  isEconomyRouteActive(): boolean {
    return this.router.url.includes('/economy');
  }

  isAdminMenuActive(): boolean {
    this.isAdminMenuSelected = this.router.url.includes('/admin');
    return this.router.url.includes('/admin');
  }

  isBatchBillingMenuVisible(): boolean {
    return (
      this.isAuthenticated() &&
      !!this.supplierProfiles?.some(s => s.type === SidenavComponent.BATCH_BILLING_TYPE_ID) &&
      !!this.accountRoleService?.roles.canCreateEconomyPageForTravel
    );
  }

  changeLanguage(languageKey: string): void {
    this.sessionStorage.store('locale', languageKey);
    this.translateService.use(languageKey);
  }

  isAuthenticated(): boolean {
    return this.accountService.isAuthenticated();
  }

  toggleAdminMenu(): void {
    this.isAdminMenuSelected = !this.isAdminMenuSelected;
  }

  resetFilterRequest(): void {
    this.filterRequestService.init();
  }

  profiles(): string[] {
    return this.accountService.getSupplierProfiles();
  }

  private loadProfiles(): void {
    if (this.isAuthenticated()) {
      this.subscriptions.push(
        this.customerProfileService.getCustomerProfiles().pipe(take(1)).subscribe({
          next: (response: CustomerProfiles[]) => {
            this.customerProfiles = response
              ?.map(profile => this.customerProfilesAdapter.adapt(profile))
              .sort((a, b) => a.name.localeCompare(b.name));
          },
          error: err => {
            this.error = err;
            this.logger.debug(err);
          }
        })
      );
    }
  }

  private loadSupplierProfiles(): void {
    if (this.isAuthenticated()) {
      this.subscriptions.push(
        this.supplierProfileService.getSupplierProfiles().pipe(take(1)).subscribe(supplierProfiles =>
          this.supplierProfiles = supplierProfiles
        )
      );
    }
  }

  private loadSmsQrDraftCaseCount(): void {
    if (this.isAuthenticated()) {
      this.subscriptions.push(
        this.supoCaseInvoiceService
          .getCases()
          .pipe(take(1))
          .subscribe(
            cases => (this.smsQrDraftCaseCount = cases.filter(draft => draft.cccBridgeToken === null && draft.createdByEndUserRequest).length)
          )
      );
    }
  }
}
